const YOUTUBE_REGEX = /(?:youtu\.be\/|youtube(?:-nocookie)?\.com\/(?:watch\?v=|shorts\/|embed\/))([A-Za-z0-9_-]{11})/;
const SCROLL_LOCK_COUNT_KEY = 'mediaPlayerScrollLockCount';
const SCROLL_LOCK_Y_KEY = 'mediaPlayerScrollY';
const SCROLL_LOCK_PREV_POSITION_KEY = 'mediaPlayerPrevPosition';
const SCROLL_LOCK_PREV_TOP_KEY = 'mediaPlayerPrevTop';
const SCROLL_LOCK_PREV_LEFT_KEY = 'mediaPlayerPrevLeft';
const SCROLL_LOCK_PREV_RIGHT_KEY = 'mediaPlayerPrevRight';
const SCROLL_LOCK_PREV_WIDTH_KEY = 'mediaPlayerPrevWidth';

const lockPageScroll = () => {
  const body = document.body;
  const lockCount = Number(body.dataset[SCROLL_LOCK_COUNT_KEY] || '0');

  if (lockCount === 0) {
    const scrollY = window.scrollY || window.pageYOffset || 0;

    body.dataset[SCROLL_LOCK_Y_KEY] = String(scrollY);
    body.dataset[SCROLL_LOCK_PREV_POSITION_KEY] = body.style.position || '';
    body.dataset[SCROLL_LOCK_PREV_TOP_KEY] = body.style.top || '';
    body.dataset[SCROLL_LOCK_PREV_LEFT_KEY] = body.style.left || '';
    body.dataset[SCROLL_LOCK_PREV_RIGHT_KEY] = body.style.right || '';
    body.dataset[SCROLL_LOCK_PREV_WIDTH_KEY] = body.style.width || '';

    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.classList.add('media-player-open');
  }

  body.dataset[SCROLL_LOCK_COUNT_KEY] = String(lockCount + 1);
};

const unlockPageScroll = () => {
  const body = document.body;
  const lockCount = Number(body.dataset[SCROLL_LOCK_COUNT_KEY] || '0');

  if (lockCount <= 1) {
    const scrollY = Number(body.dataset[SCROLL_LOCK_Y_KEY] || '0');

    body.style.position = body.dataset[SCROLL_LOCK_PREV_POSITION_KEY] || '';
    body.style.top = body.dataset[SCROLL_LOCK_PREV_TOP_KEY] || '';
    body.style.left = body.dataset[SCROLL_LOCK_PREV_LEFT_KEY] || '';
    body.style.right = body.dataset[SCROLL_LOCK_PREV_RIGHT_KEY] || '';
    body.style.width = body.dataset[SCROLL_LOCK_PREV_WIDTH_KEY] || '';
    body.classList.remove('media-player-open');

    delete body.dataset[SCROLL_LOCK_COUNT_KEY];
    delete body.dataset[SCROLL_LOCK_Y_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_POSITION_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_TOP_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_LEFT_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_RIGHT_KEY];
    delete body.dataset[SCROLL_LOCK_PREV_WIDTH_KEY];

    window.scrollTo(0, scrollY);
    return;
  }

  body.dataset[SCROLL_LOCK_COUNT_KEY] = String(lockCount - 1);
};

const isIndeeUrl = (url) => {
  if (!url) {
    return false;
  }

  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.hostname.includes('indee.tv');
  } catch (error) {
    return /indee\.tv/i.test(url);
  }
};

const followExternalLink = (url, target = '_blank') => {
  if (target === '_self') {
    window.location.assign(url);
    return;
  }

  const popup = window.open(url, target, 'noopener');
  if (popup) {
    popup.opener = null;
  }
};

const getYouTubeId = (url) => {
  if (!url) {
    return null;
  }
  const match = url.match(YOUTUBE_REGEX);
  return match ? match[1] : null;
};

const createYouTubePlayer = (youtubeId) => {
  const iframe = document.createElement('iframe');
  const youtubeSrc = `https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&playsinline=1`;
  const allowByRegion = Boolean(window.paramountCmpEmbeds?.shouldAllowSocialEmbedsByRegion?.());

  if (allowByRegion) {
    iframe.src = youtubeSrc;
  } else {
    iframe.dataset.src = youtubeSrc;
  }

  iframe.classList.add('optanon-category-5');
  iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
  iframe.allowFullscreen = true;
  iframe.title = 'YouTube video player';
  iframe.loading = 'lazy';
  return iframe;
};

const createConsentBlockedMessage = (youtubeId) => {
  const message = document.createElement('div');
  message.className = 'cmp-blocked-message';
  message.setAttribute('role', 'note');

  const description = document.createElement('p');
  description.textContent = window.paramountCmpEmbeds?.getBlockedMessage?.('optanon-category-5') || 'Please enable Social Media Cookies to view this video.';
  message.appendChild(description);

  if (youtubeId) {
    const orNode = document.createElement('span');
    orNode.className = 'cmp-blocked-or';
    orNode.textContent = 'OR';
    message.appendChild(orNode);

    const youtubeLink = document.createElement('a');
    youtubeLink.href = `https://www.youtube.com/watch?v=${youtubeId}`;
    youtubeLink.target = '_blank';
    youtubeLink.rel = 'noopener noreferrer';
    youtubeLink.textContent = 'Watch this video on YouTube';
    message.appendChild(youtubeLink);
  }

  return message;
};

const requestCmpIframeLoad = () => {
  window.dispatchEvent(new Event('paramount:cmp-load-iframes'));

  if (window.cbsoptanon && Array.isArray(window.cbsoptanon.cmd)) {
    window.cbsoptanon.cmd.push((cmp) => {
      if (!cmp?.tags) {
        return;
      }

      if (typeof cmp.tags.load === 'function') {
        cmp.tags.load('IFRAME');
      }

      if (typeof cmp.tags.loadDeferredTarget === 'function') {
        cmp.tags.loadDeferredTarget('optanon-category-5', 'IFRAME', 'src');
      }
    });
  }
};

const createVideoPlayer = (url) => {
  const video = document.createElement('video');
  video.src = url;
  video.controls = true;
  video.autoplay = true;
  video.playsInline = true;
  video.muted = true;
  return video;
};

const videoHasAudioTrack = (video) => {
  if (!video || video.tagName !== 'VIDEO') {
    return false;
  }

  if (video.audioTracks && typeof video.audioTracks.length === 'number') {
    return video.audioTracks.length > 0;
  }

  if (typeof video.mozHasAudio === 'boolean') {
    return video.mozHasAudio;
  }

  if (typeof video.webkitAudioDecodedByteCount === 'number') {
    return video.webkitAudioDecodedByteCount > 0;
  }

  if (typeof video.captureStream === 'function') {
    try {
      const stream = video.captureStream();
      return stream.getAudioTracks().length > 0;
    } catch (error) {
      return false;
    }
  }

  return false;
};

const setupMediaPlayer = (root, openOnLoad = false) => {
  const trigger = root.querySelector('.media-player-trigger');
  const modal = root.querySelector('.media-player-modal');
  const stage = root.querySelector('[data-media-stage]');
  const closeButtons = root.querySelectorAll('[data-media-close]');
  const muteButton = root.querySelector('[data-media-mute]');
  const mediaUrl = root.getAttribute('data-media-url') || '';
  const mediaTarget = root.getAttribute('data-media-target') || '_blank';
  const closeUrl = root.getAttribute('data-media-close-url');
  const closeHistoryUrl = root.getAttribute('data-media-close-history-url');

  if ((!trigger && !openOnLoad) || !modal || !stage || !mediaUrl) {
    return;
  }

  let activePlayer = null;

  const clearPlayer = () => {
    if (!activePlayer) {
      return;
    }

    if (activePlayer.tagName === 'VIDEO') {
      activePlayer.pause();
      activePlayer.removeAttribute('src');
      activePlayer.load();
    }

    stage.innerHTML = '';
    activePlayer = null;
    muteButton?.classList.add('is-hidden');
    muteButton?.classList.remove('is-unmuted');
  };

  const setMuteVisibilityForVideo = (video) => {
    if (!muteButton) {
      return;
    }

    const hasAudioTrack = videoHasAudioTrack(video);
    muteButton.classList.toggle('is-hidden', !hasAudioTrack);

    if (!hasAudioTrack) {
      muteButton.classList.remove('is-unmuted');
    }
  };

  const monitorVideoAudioTrack = (video) => {
    if (!video || video.tagName !== 'VIDEO') {
      return;
    }

    const updateMuteVisibility = () => {
      if (video !== activePlayer) {
        return;
      }

      setMuteVisibilityForVideo(video);
    };

    updateMuteVisibility();
    video.addEventListener('loadedmetadata', updateMuteVisibility);
    video.addEventListener('playing', updateMuteVisibility);
    video.addEventListener('timeupdate', updateMuteVisibility);
  };

  const openModal = () => {
    const youtubeId = getYouTubeId(mediaUrl);

    clearPlayer();
    root.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    lockPageScroll();

    if (youtubeId) {
      const allowSocialEmbed = Boolean(window.paramountCmpEmbeds?.shouldAllowSocialEmbedsByRegion?.());
      activePlayer = allowSocialEmbed ? createYouTubePlayer(youtubeId) : createConsentBlockedMessage(youtubeId);
      muteButton?.classList.add('is-hidden');
    } else {
      activePlayer = createVideoPlayer(mediaUrl);
      muteButton?.classList.add('is-hidden');
      muteButton?.classList.remove('is-unmuted');
      monitorVideoAudioTrack(activePlayer);
    }

    stage.appendChild(activePlayer);

    if (youtubeId) {
      requestCmpIframeLoad();
    }
  };

  const closeModal = () => {
    const activeElement = document.activeElement;
    if (activeElement && modal.contains(activeElement)) {
      trigger?.focus();
    }

    root.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    unlockPageScroll();
    clearPlayer();

    if (closeHistoryUrl) {
      window.history.replaceState(null, '', closeHistoryUrl);
    }

    if (closeUrl) {
      window.location.assign(closeUrl);
    }
  };

  const toggleMute = () => {
    if (!activePlayer || activePlayer.tagName !== 'VIDEO' || muteButton?.classList.contains('is-hidden')) {
      return;
    }

    activePlayer.muted = !activePlayer.muted;
    muteButton?.classList.toggle('is-unmuted', !activePlayer.muted);
  };

  trigger?.addEventListener('click', (event) => {
    event.preventDefault();

    if (isIndeeUrl(mediaUrl)) {
      followExternalLink(mediaUrl, '_blank');
      return;
    }

    openModal();
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', (event) => {
      event.preventDefault();
      closeModal();
    });
  });

  muteButton?.addEventListener('click', (event) => {
    event.preventDefault();
    toggleMute();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root.classList.contains('is-open')) {
      closeModal();
    }
  });

  if (openOnLoad && !isIndeeUrl(mediaUrl)) {
    openModal();
  }
};

document.addEventListener('DOMContentLoaded', () => {
  const query = new URLSearchParams(window.location.search);
  const videoSlug = query.get('video');
  const players = Array.from(document.querySelectorAll('[data-media-player]'));
  const linkedVideo = videoSlug
    ? players.find((root) => root.getAttribute('data-media-slug') === videoSlug)
    : null;
  const linkedPlayer = linkedVideo || (query.has('trailer')
    ? players.find((root) => root.hasAttribute('data-media-trailer'))
    : null);

  players.forEach((root) => {
    setupMediaPlayer(root, root === linkedPlayer || root.hasAttribute('data-media-auto-open'));
  });
});
